import React, { useEffect, useRef, useState } from 'react';
import PortalCanvas from '../canvas/PortalCanvas';
import { ago } from '../../../utils/surfaceFormat';
import { SquareAvatar, MutedIcon, SoundIcon, MAGENTA } from './PortalPageChrome';

/**
 * The Portal as the viewer draws it, standing on its own page: the picture (or the text
 * Portal's background), the words and the venue at their saved places, the progress bar and
 * the sender's header. Composer v2 put the words on the picture, so there is no slab — on a
 * desktop the room chip and the count sit at the bottom over a scrim, where the viewer's
 * buttons would be.
 *
 * On a phone the card is the same 9:16, full-bleed: the words and the venue sit where the host
 * placed them, and the title block starts under the card rather than riding up over it (that
 * overlap was designed for the old card, which carried no words).
 */
export default function PortalCard({
  signal,
  media = null,          // StoryItemDto (kind video|image) or null for a text Portal
  mobile = false,
  muted = true,
  onToggleMute = null,
  roomLabel = 'FRIENDS',
  chip = '',             // the time chip under the words
  venue = null,          // { name, address } — only for people who may see it
  opacity = 1,
  children = null,       // anything laid over the card (the editor's REPLACE button)
  frameHandle = null,    // the editor's drag-to-frame layer
  frame = undefined,     // a draft frame that overrides the media's own
  showHeader = true,     // the editor drops the sender's row; its REPLACE button stands there
  layoutEditor = null,   // { onLayout, ... } — the page's editor makes the card the layout editor
  text = undefined,      // the editor's draft words, over the signal's own
  event = undefined,     // the linked event row, over the signal's own sourceEvent
  venueDate = null,      // { mon, day, time } — the window, for the venue's DATE style
}) {
  const boxRef = useRef(null);
  const [progress, setProgress] = useState(media?.kind === 'video' ? 0 : 1);
  const isVideo = media?.kind === 'video' && !!media?.mediaUrl;

  // The bar follows playback inside the trimmed window; a photo's bar is full.
  useEffect(() => {
    if (!isVideo) { setProgress(1); return undefined; }
    setProgress(0);
    let v = null;
    let raf = 0;
    const onTime = () => {
      const start = (media.trimStartMs || 0) / 1000;
      const end = media.trimEndMs > media.trimStartMs ? media.trimEndMs / 1000 : (v.duration || 0);
      const span = end - start;
      if (span > 0) setProgress(Math.max(0, Math.min(1, (v.currentTime - start) / span)));
    };
    const attach = () => {
      v = boxRef.current?.querySelector('video');
      if (!v) { raf = requestAnimationFrame(attach); return; }
      v.addEventListener('timeupdate', onTime);
    };
    attach();
    return () => { cancelAnimationFrame(raf); v?.removeEventListener('timeupdate', onTime); };
  }, [isVideo, media?.mediaUrl, media?.trimStartMs, media?.trimEndMs]);

  const handle = signal?.hostHandle || signal?.hostDisplayName || '';
  const age = signal?.createdAtUtc ? ago(signal.createdAtUtc) : '';

  return (
    <div
      ref={boxRef}
      data-testid="portal-card"
      className="relative w-full overflow-hidden"
      style={{
        aspectRatio: '9 / 16',
        background: '#000',
        border: mobile ? 0 : '2px solid #fff',
        opacity,
        transition: 'opacity .3s',
      }}
    >
      <PortalCanvas
        media={media}
        text={text !== undefined ? text : (signal?.signalText || '')}
        chip={chip}
        layout={signal?.layout || null}
        venue={venue}
        venueDate={venueDate}
        event={event !== undefined ? event : (signal?.sourceEvent || null)}
        eventLinkTo={signal?.sourceEvent?.inviteCode ? `/events/${signal.sourceEvent.inviteCode}` : null}
        frame={frame}
        muted={muted}
        active
        editor={layoutEditor}
        className="absolute inset-0"
      >
        {frameHandle}
      </PortalCanvas>

      <div className="absolute inset-x-0" style={{ top: 10, padding: '0 8px', zIndex: 6, pointerEvents: 'none' }}>
        <div aria-hidden style={{ height: 2, background: 'rgba(255,255,255,.3)' }}>
          <div data-testid="portal-progress" style={{ height: 2, width: `${Math.round(progress * 100)}%`, background: '#fff', transition: 'width 80ms linear' }} />
        </div>
        {showHeader && (
        <div className="flex items-start justify-between" style={{ marginTop: 14, padding: '0 8px', gap: 10 }}>
          <div className="flex min-w-0 items-start" style={{ gap: 10 }}>
            <SquareAvatar url={signal?.hostAvatarUrl} name={signal?.hostDisplayName} size={34} />
            <div className="min-w-0 font-mono text-white">
              <div className="truncate" style={{ fontSize: 13 }}>
                <strong>{handle}</strong>
                {age && <span style={{ marginLeft: 8, color: 'rgba(255,255,255,.7)' }}>{age}</span>}
              </div>
              <div className="font-bold uppercase" style={{ marginTop: 2, fontSize: 10, letterSpacing: '0.1em', color: MAGENTA }}>
                Portal · {signal?.mode === 'curated' ? 'Curated' : 'Open'}
              </div>
            </div>
          </div>
          {isVideo && onToggleMute && (
            <button
              type="button"
              onClick={onToggleMute}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="flex flex-shrink-0 items-center justify-center text-white"
              style={{ width: 32, height: 32, pointerEvents: 'auto' }}
            >
              {muted ? <MutedIcon /> : <SoundIcon />}
            </button>
          )}
        </div>
        )}
      </div>

      {children}

      {!layoutEditor && (
        <>
          <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0" style={{ height: '34%', background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,.85) 40%, #000)', zIndex: 4 }} />
          <div className="absolute flex items-center font-mono font-bold uppercase text-white" style={{ left: 16, bottom: 16, gap: 8, fontSize: 10, letterSpacing: '0.1em', zIndex: 5 }}>
            <span style={{ border: '2px solid #fff', padding: '2px 8px' }}>{roomLabel}</span>
            <span>{signal?.acceptedCount ?? 0} IN · {(signal?.mode || 'open').toUpperCase()}</span>
          </div>
        </>
      )}
    </div>
  );
}
